'use client'

import Image from 'next/image'
import { ArrowLeft, EllipsisVertical, Trash2, Video } from 'lucide-react'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { SANTA_IDLE_POSTER } from '@/lib/santa-chat/gestures.manifest'
import { cn } from '@/lib/utils'

export interface ChatHeaderLabels {
  back: string
  title: string
  santaRecording: string
  deleteHistory: string
  moreOptions: string
  callCta: string
}

export interface ChatHeaderActions {
  back: () => void
  call: () => void
  /** Ausente mientras se confirma el borrado (la confirmación ocupa su lugar). */
  deleteHistory?: () => void
}

interface ChatHeaderProps {
  labels: ChatHeaderLabels
  santaRecording: boolean
  actions: ChatHeaderActions
  /** La cabecera es lo primero de la pantalla y deja sitio a la muesca; con una barra encima, no. */
  safeAreaTop?: boolean
}

/**
 * Borrar vive dentro de un menú «⋮» y no como papelera suelta: un niño que
 * toca todo no se topa con la confirmación de borrado por accidente.
 */
function MoreMenu({ labels, santaRecording, onDelete }: { labels: ChatHeaderLabels; santaRecording: boolean; onDelete: () => void }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={labels.moreOptions}
          className="flex size-10 items-center justify-center rounded-full text-muted-foreground"
        >
          <EllipsisVertical aria-hidden className="size-5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem variant="destructive" disabled={santaRecording} onSelect={onDelete}>
          <Trash2 aria-hidden className="size-4" />
          {labels.deleteHistory}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Cabecera del chat: volver, Papá Noel, menú con borrar (no mientras contesta) y llamar. */
export function ChatHeader({ labels, santaRecording, actions, safeAreaTop = true }: ChatHeaderProps) {
  return (
    <header className={cn('flex shrink-0 items-center gap-2 bg-card px-3 py-2', safeAreaTop && 'pt-[max(env(safe-area-inset-top),0.5rem)]')}>
      <button type="button" aria-label={labels.back} onClick={actions.back} className="flex size-10 items-center justify-center rounded-full">
        <ArrowLeft aria-hidden className="size-6" />
      </button>
      <Image src={SANTA_IDLE_POSTER} alt="" width={40} height={40} className="size-10 rounded-full object-cover" />
      <div className="min-w-0 flex-1">
        <h1 className="font-christmas truncate text-xl font-bold">{labels.title}</h1>
        {santaRecording && <p className="readable-body truncate text-xs text-accent">{labels.santaRecording}</p>}
      </div>
      {actions.deleteHistory && (
        <MoreMenu labels={labels} santaRecording={santaRecording} onDelete={actions.deleteHistory} />
      )}
      <button
        type="button"
        aria-label={labels.callCta}
        onClick={actions.call}
        className="flex size-11 items-center justify-center rounded-full bg-secondary text-secondary-foreground"
      >
        <Video aria-hidden className="size-6" />
      </button>
    </header>
  )
}
